import type { ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { Flex, Radio, Spinner } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEstimatedDeliveryDate } from '../../../../../bff-api/delivery/useEstimatedDeliveryDate';
import { Text } from '../../../../../ui-components';
import { useCustomPreferences } from '../../../../../utils/api';
import { CustomPreference } from '../../../../../utils/api/CustomPreference';
import { isDropship } from '../../../../../utils/product-utils';
import { useBasketProductState } from '../../../hooks/useBasketProductState';
import type { ProductItem, ShippingOption } from '../../../../../bff-api/baskets/model';
import type { ProductInventory } from '../../../../../bff-api/baskets/model/BasketInventory';

interface Props {
    id: string;
    productItem: ProductItem;
    zipCode: string;
    option?: ShippingOption;
    inventory?: ProductInventory;
    onOpenZipCode: () => void;
    isLoading: boolean;
}

export const CartShipToOption = ({
    id,
    productItem,
    zipCode,
    option,
    inventory,
    onOpenZipCode,
    isLoading,
}: Props): ReactElement | null => {
    const intl = useIntl();
    const { eddEnable = true, defaultEddMessage = '' } = useCustomPreferences([
        CustomPreference.EDD_ENABLE,
        CustomPreference.DEFAULT_EDD_MESSAGE,
    ]);
    const { getStateForProduct } = useBasketProductState();
    const { data = {} } = useEstimatedDeliveryDate(zipCode);
    const { edd } = data;

    const shipToComponent = useCallback(
        (chunks: ReactNode[]) => (
            <Text
                id={`${id}-zip-code`}
                size="small"
                style="bold"
                type="link"
                onClick={onOpenZipCode}
            >
                {chunks}
            </Text>
        ),
        [id, onOpenZipCode],
    );

    if (!option || !inventory) {
        return null;
    }

    const hasEnoughQuantity = inventory?.onlineInventory?.availableToSell >= productItem.quantity;

    const isDropShip = isDropship(productItem);
    const product = getStateForProduct(productItem.productId)?.product;
    let dropshipMessage;
    if (product?.c_dropShipEligibility) {
        if (product.c_dropshipEstimatedDeliveryDate) {
            dropshipMessage = intl.formatMessage(
                {
                    id: 'delivery.options.arrives.in.date',
                    defaultMessage: 'Arrives in {date}',
                },
                {
                    date: product.c_dropshipEstimatedDeliveryDate,
                },
            );
        } else if (product.c_dropshipEstimatedProcessingDate) {
            dropshipMessage = intl.formatMessage(
                {
                    id: 'delivery.options.arrives.in.date.processing',
                    defaultMessage: 'Ships in {date}',
                },
                {
                    date: product.c_dropshipEstimatedProcessingDate,
                },
            );
        }
    }

    return hasEnoughQuantity && productItem.c_online ? (
        <Flex data-testid={`${id}-available`} gap={2} alignItems="start">
            {isLoading ? (
                <Spinner data-testid={`${id}-spinner`} size="sm" marginTop={1} />
            ) : (
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} />
            )}

            <Flex direction="column">
                <Text id={`${id}-label`} style="bold">
                    <FormattedMessage id="cart.section.ship-it.title" defaultMessage="Ship It" />
                </Text>

                {isDropShip && dropshipMessage ? (
                    <Text id={`${id}-dropship-message`} size="small">
                        {dropshipMessage}
                    </Text>
                ) : null}

                {eddEnable &&
                    !isDropShip &&
                    (zipCode ? (
                        <>
                            <Text id={`${id}-ship-to-zip-code`}>
                                <FormattedMessage
                                    id="edd.ship.to.zipCode"
                                    defaultMessage="Ship to <shipToComponent>{zipCode}</shipToComponent>"
                                    values={{ shipToComponent, zipCode }}
                                />
                            </Text>

                            {edd !== undefined ? (
                                <Text
                                    id={`${id}-edd-day-delivery`}
                                    size="small"
                                    color="slt-success-green.1000"
                                >
                                    <FormattedMessage
                                        id="edd.day.delivery"
                                        defaultMessage="{edd} day delivery"
                                        values={{ edd }}
                                    />
                                </Text>
                            ) : null}
                        </>
                    ) : (
                        <Text
                            id={`${id}-enter-zip-code`}
                            size="small"
                            style="bold"
                            type="link"
                            onClick={onOpenZipCode}
                        >
                            <FormattedMessage
                                id="edd.enter.zip.code"
                                defaultMessage="Enter your Zip Code"
                            />
                        </Text>
                    ))}

                {!eddEnable && !isDropShip ? (
                    <Text id={`${id}-edd-day-delivery`} size="small" color="slt-success-green.1000">
                        {defaultEddMessage}
                    </Text>
                ) : null}
            </Flex>
        </Flex>
    ) : (
        <Flex data-testid={`${id}-not-available`} gap={2} alignItems="start" opacity={0.5}>
            <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

            <Flex direction="column">
                <Text id={`${id}-label`} style="bold">
                    <FormattedMessage id="cart.section.ship-it.title" defaultMessage="Ship It" />
                </Text>

                <Text id={`${id}-not-available-label`}>
                    <FormattedMessage id="not.available" defaultMessage="Not Available" />
                </Text>
            </Flex>
        </Flex>
    );
};

CartShipToOption.defaultProps = {
    option: undefined,
    inventory: undefined,
};
